import { inject, Injectable } from '@angular/core';
import { INDEXED_DB_CONFIG } from '@shared/services';
import { DataTableEnum } from '@shared/services/indexdb/enum';
import { LogsService } from '@shared/services/logs';
import { PlatformContextService } from '@shared/services/platform-context';
import { firstValueFrom, from, Observable, of, switchMap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class IndexedDbService {
  private db: IDBDatabase | null = null;
  private opening?: Promise<boolean>;

  private readonly platformContextService = inject(PlatformContextService);
  private readonly dbConfig: [string, number] = inject(INDEXED_DB_CONFIG);
  private readonly logs = inject(LogsService);

  addOrUpdateData<T>(tableName: keyof typeof DataTableEnum, key: string, value: T): Observable<void> {
    if (this.platformContextService.isServer()) return of(void 0);
    return from(this.ensureReady()).pipe(
      switchMap((ok) => {
        if (!ok || !this.db) {
          this.logs.warn('IndexedDbService', 'IndexedDB not connected', {
            operation: 'addOrUpdateData',
            tableName,
            key,
          });
          return of(void 0);
        }
        const transaction = this.db.transaction(tableName, 'readwrite');
        const store = transaction.objectStore(tableName);
        const request = key ? store.put(value, key) : store.add(value);
        return new Observable<void>((observer) => {
          request.onsuccess = () => {
            observer.next();
            observer.complete();
          };
          request.onerror = () => observer.error(request.error);
        });
      })
    );
  }

  getData<T>(tableName: keyof typeof DataTableEnum, key?: string): Observable<T | undefined> {
    if (this.platformContextService.isServer()) return of(undefined as T | undefined);
    return from(this.ensureReady()).pipe(
      switchMap((ok) => {
        if (!ok || !this.db) {
          this.logs.warn('IndexedDbService', 'IndexedDB not connected', { operation: 'getData', tableName, key });
          return of(undefined as T | undefined);
        }

        const transaction = this.db.transaction(tableName, 'readonly');
        const store = transaction.objectStore(tableName);
        const request = key ? store.get(key) : store.getAll();

        return new Observable<T | undefined>((observer) => {
          request.onsuccess = () => {
            observer.next(request.result as T);
            observer.complete();
          };
          request.onerror = () => observer.error(request.error);
        });
      })
    );
  }

  listKeys(tableName: keyof typeof DataTableEnum): Observable<string[]> {
    if (this.platformContextService.isServer()) return of([]);
    return from(this.ensureReady()).pipe(
      switchMap((ok) => {
        if (!ok || !this.db) {
          this.logs.warn('IndexedDbService', 'IndexedDB not connected', { operation: 'listKeys', tableName });
          return of([] as string[]);
        }

        const transaction = this.db.transaction(tableName, 'readonly');
        const store = transaction.objectStore(tableName);
        const request = store.getAllKeys();

        return new Observable<string[]>((observer) => {
          request.onsuccess = () => {
            observer.next(request.result as string[]);
            observer.complete();
          };
          request.onerror = () => observer.error(request.error);
        });
      })
    );
  }

  deleteKey(tableName: keyof typeof DataTableEnum, key: string): Observable<void> {
    if (this.platformContextService.isServer()) return of(void 0);
    return from(this.ensureReady()).pipe(
      switchMap((ok) => {
        if (!ok || !this.db) {
          this.logs.warn('IndexedDbService', 'IndexedDB not connected', { operation: 'deleteKey', tableName, key });
          return of(void 0);
        }

        const transaction = this.db.transaction(tableName, 'readwrite');
        const store = transaction.objectStore(tableName);
        const request = store.delete(key);

        return new Observable<void>((observer) => {
          request.onsuccess = () => {
            observer.next();
            observer.complete();
          };
          request.onerror = () => observer.error(request.error);
        });
      })
    );
  }

  clearTable(tableName: keyof typeof DataTableEnum): Observable<void> {
    if (this.platformContextService.isServer()) return of(void 0);
    return from(this.ensureReady()).pipe(
      switchMap((ok) => {
        if (!ok || !this.db) {
          this.logs.warn('IndexedDbService', 'IndexedDB not connected', { operation: 'clearTable', tableName });
          return of(void 0);
        }

        const transaction = this.db.transaction(tableName, 'readwrite');
        const store = transaction.objectStore(tableName);
        const request = store.clear();

        return new Observable<void>((observer) => {
          request.onsuccess = () => {
            observer.next();
            observer.complete();
          };
          request.onerror = () => observer.error(request.error);
        });
      })
    );
  }

  deleteDatabase(): Observable<void> {
    if (this.platformContextService.isServer()) return of(void 0);
    return from(this.ensureReady()).pipe(
      switchMap(() => {
        if (this.db) {
          this.db.close();
          this.db = null;
        }

        return new Observable<void>((observer) => {
          const request = indexedDB.deleteDatabase(this.dbConfig[0]);

          request.onsuccess = () => {
            this.opening = undefined;
            this.db = null;
            observer.next();
            observer.complete();
          };
          request.onerror = () => observer.error(request.error);
        });
      })
    );
  }

  connectToDB(): Observable<boolean> {
    if (this.platformContextService.isServer()) return of(true);

    if (!this.opening) {
      this.opening = new Promise<boolean>((resolve) => {
        const [dbName, dbVersion] = this.dbConfig;
        const request = indexedDB.open(dbName, dbVersion);

        request.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
          this.removeObsoleteObjectStores(db);
          Object.keys(DataTableEnum)
            .filter((key) => isNaN(Number(key)))
            .forEach((tableName) => {
              if (!db.objectStoreNames.contains(tableName)) {
                db.createObjectStore(tableName);
              }
            });
        };

        request.onsuccess = (event) => {
          this.db = (event.target as IDBOpenDBRequest).result;
          this.db.onversionchange = () => {
            this.logs.warn('IndexedDbService', 'Version change detected, closing DB');
            this.db?.close();
            this.db = null;
            this.opening = undefined;
          };
          this.logs.info('IndexedDbService', 'Database connected');
          resolve(true);
        };

        request.onerror = () => {
          this.logs.error('IndexedDbService', 'Database error:', request.error);
          resolve(false);
        };

        request.onblocked = () => {
          this.logs.warn('IndexedDbService', 'Database open blocked by another tab');
        };
      });
    }

    return from(this.opening);
  }

  private async ensureReady(): Promise<boolean> {
    if (this.db) return true;
    if (this.platformContextService.isServer()) return true;
    return this.opening ?? (this.opening = firstValueFrom(this.connectToDB()));
  }

  private removeObsoleteObjectStores(db: IDBDatabase): void {
    if (this.platformContextService.isServer()) return;
    const existingObjectStores = Array.from(db.objectStoreNames);
    const requiredObjectStores = Object.keys(DataTableEnum).filter((key) => isNaN(Number(key)));
    const obsoleteObjectStores = existingObjectStores.filter((store) => !requiredObjectStores.includes(store));

    obsoleteObjectStores.forEach((storeName) => {
      db.deleteObjectStore(storeName);
    });
  }
}
